import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat as Seat } from '../bridge/seats.js';
import type { Card, Rank } from '../trick/cards.js';
import type { TrickPlay } from '../trick/rules.js';
import {
  type HeartsCompletedTrick,
  type HeartsGame,
  type HeartsPhase,
  type PassDirection,
  heartsLegalPlays,
  heartsRules,
  passTarget,
} from './play.js';

/**
 * Everything one seat may see: its own hand, how many cards the others hold, the passing direction, whether each
 * seat has chosen a pass (but never WHAT anyone chose, except its own), the cards it received, the tricks played,
 * hearts broken, and the points taken so far. No other hand is ever included.
 */
export interface HeartsView {
  readonly viewer: Seat;
  readonly phase: HeartsPhase;
  readonly handNumber: number;
  readonly minRank: Rank;
  /** How many cards to pass: 3, or 2 with Big Cards. */
  readonly passCount: number;
  readonly controllers: BridgeControllers;
  readonly hand: readonly Card[];
  readonly handSizes: Readonly<Record<Seat, number>>;
  readonly passDirection: PassDirection;
  /** The seat the viewer's three cards go to (the viewer itself when nothing is passed). */
  readonly passTo: Seat;
  /** The viewer's own chosen pass, if made. */
  readonly ownPass: readonly Card[] | null;
  /** Which seats have chosen (public: it is only known THAT they chose). */
  readonly hasPassed: Readonly<Record<Seat, boolean>>;
  /** The cards the viewer received in the pass (empty before the exchange). */
  readonly received: readonly Card[];
  readonly trick: readonly TrickPlay<Seat>[];
  readonly tricks: readonly HeartsCompletedTrick[];
  readonly heartsBroken: boolean;
  readonly pointsTaken: Readonly<Record<Seat, number>>;
  readonly turn: Seat;
  /** True when the viewer must choose a pass, or play a card, now. */
  readonly canAct: boolean;
  readonly legalPlays: readonly Card[];
}

export function heartsViewFor(game: HeartsGame, viewer: Seat): HeartsView {
  const mustPass = game.phase === 'pass' && game.passes[viewer] === undefined;
  const mustPlay = game.phase === 'play' && game.turn === viewer;
  return {
    viewer,
    phase: game.phase,
    handNumber: game.handNumber,
    minRank: game.minRank,
    passCount: heartsRules(game.minRank).passCount,
    controllers: game.controllers,
    hand: game.hands[viewer],
    handSizes: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, game.hands[s].length])) as Record<Seat, number>,
    passDirection: game.passDirection,
    passTo: passTarget(viewer, game.passDirection),
    ownPass: game.passes[viewer] ?? null,
    hasPassed: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, game.passes[s] !== undefined])) as Record<Seat, boolean>,
    received: game.received[viewer],
    trick: game.trick,
    tricks: game.tricks,
    heartsBroken: game.heartsBroken,
    pointsTaken: game.pointsTaken,
    turn: game.turn,
    canAct: mustPass || mustPlay,
    legalPlays: mustPlay ? heartsLegalPlays(game) : [],
  };
}
